import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { RotateCcw, Flag, Bomb, Trophy, Skull } from 'lucide-react';

interface Cell {
  isMine: boolean;
  isRevealed: boolean;
  isFlagged: boolean;
  neighborMines: number;
}

interface MinesweeperProps {
  onClose?: () => void;
}

export const Minesweeper: React.FC<MinesweeperProps> = ({ onClose }) => {
  const [board, setBoard] = useState<Cell[][]>([]);
  const [gameState, setGameState] = useState<'playing' | 'won' | 'lost'>('playing');
  const [mineCount, setMineCount] = useState(10);
  const [flagCount, setFlagCount] = useState(0);
  const [timer, setTimer] = useState(0);
  const [firstClick, setFirstClick] = useState(true);

  const ROWS = 9;
  const COLS = 9;
  const MINES = 10;

  useEffect(() => {
    initializeGame();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (gameState === 'playing' && !firstClick) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameState, firstClick]);

  const createEmptyBoard = (): Cell[][] => {
    return Array(ROWS).fill(null).map(() =>
      Array(COLS).fill(null).map(() => ({
        isMine: false,
        isRevealed: false,
        isFlagged: false,
        neighborMines: 0
      }))
    );
  };

  const placeMines = (board: Cell[][], firstClickRow: number, firstClickCol: number): Cell[][] => {
    const newBoard = board.map(row => row.map(cell => ({ ...cell })));
    let minesPlaced = 0;

    while (minesPlaced < MINES) {
      const row = Math.floor(Math.random() * ROWS);
      const col = Math.floor(Math.random() * COLS);

      // Don't place mine on first click or if already has mine
      if (!newBoard[row][col].isMine && !(row === firstClickRow && col === firstClickCol)) {
        newBoard[row][col].isMine = true;
        minesPlaced++;
      }
    }

    // Calculate neighbor mines
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        if (!newBoard[row][col].isMine) {
          newBoard[row][col].neighborMines = countNeighborMines(newBoard, row, col);
        }
      }
    }

    return newBoard;
  };

  const countNeighborMines = (board: Cell[][], row: number, col: number): number => {
    let count = 0;
    for (let r = Math.max(0, row - 1); r <= Math.min(ROWS - 1, row + 1); r++) {
      for (let c = Math.max(0, col - 1); c <= Math.min(COLS - 1, col + 1); c++) {
        if (r !== row || c !== col) {
          if (board[r][c].isMine) count++;
        }
      }
    }
    return count;
  };

  const initializeGame = () => {
    setBoard(createEmptyBoard());
    setGameState('playing');
    setFlagCount(0);
    setTimer(0);
    setFirstClick(true);
  };

  const revealCell = useCallback((row: number, col: number) => {
    if (gameState !== 'playing') return;

    setBoard(prevBoard => {
      let newBoard = prevBoard.map(row => row.map(cell => ({ ...cell })));

      if (firstClick) {
        newBoard = placeMines(newBoard, row, col);
        setFirstClick(false);
      }

      if (newBoard[row][col].isFlagged || newBoard[row][col].isRevealed) {
        return newBoard;
      }

      if (newBoard[row][col].isMine) {
        // Game over - reveal all mines
        for (let r = 0; r < ROWS; r++) {
          for (let c = 0; c < COLS; c++) {
            if (newBoard[r][c].isMine) {
              newBoard[r][c].isRevealed = true;
            }
          }
        }
        setGameState('lost');
        return newBoard;
      }

      // Flood fill for empty cells
      const toReveal: [number, number][] = [[row, col]];
      const revealed = new Set<string>();

      while (toReveal.length > 0) {
        const [r, c] = toReveal.pop()!;
        const key = `${r}-${c}`;

        if (revealed.has(key) || r < 0 || r >= ROWS || c < 0 || c >= COLS) continue;
        if (newBoard[r][c].isFlagged || newBoard[r][c].isRevealed || newBoard[r][c].isMine) continue;

        revealed.add(key);
        newBoard[r][c].isRevealed = true;

        // If this cell has no neighboring mines, reveal neighbors
        if (newBoard[r][c].neighborMines === 0) {
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              toReveal.push([r + dr, c + dc]);
            }
          }
        }
      }

      // Check win condition
      let revealedCount = 0;
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          if (newBoard[r][c].isRevealed && !newBoard[r][c].isMine) {
            revealedCount++;
          }
        }
      }

      if (revealedCount === ROWS * COLS - MINES) {
        setGameState('won');
      }

      return newBoard;
    });
  }, [gameState, firstClick]);

  const toggleFlag = (e: React.MouseEvent, row: number, col: number) => {
    e.preventDefault();
    if (gameState !== 'playing' || board[row][col].isRevealed) return;

    setBoard(prevBoard => {
      const newBoard = prevBoard.map(row => row.map(cell => ({ ...cell })));
      newBoard[row][col].isFlagged = !newBoard[row][col].isFlagged;
      return newBoard;
    });

    setFlagCount(prev => board[row][col].isFlagged ? prev - 1 : prev + 1);
  };

  const getCellContent = (cell: Cell) => {
    if (cell.isFlagged) {
      return <Flag className="w-4 h-4 text-red-500" />;
    }
    
    if (!cell.isRevealed) {
      return null;
    }

    if (cell.isMine) {
      return <Bomb className="w-4 h-4 text-red-600" />;
    }

    if (cell.neighborMines > 0) {
      const colors = [
        '', 'text-blue-600', 'text-green-600', 'text-red-600',
        'text-purple-600', 'text-yellow-600', 'text-pink-600',
        'text-black', 'text-gray-600'
      ];
      return (
        <span className={`font-bold ${colors[cell.neighborMines]}`}>
          {cell.neighborMines}
        </span>
      );
    }

    return null;
  };

  const getCellStyle = (cell: Cell) => {
    if (!cell.isRevealed) {
      return 'bg-gray-400 hover:bg-gray-300 border-gray-500 border-t-gray-200 border-l-gray-200';
    }
    
    if (cell.isMine) {
      return 'bg-red-500 border-red-600';
    }
    
    return 'bg-gray-200 border-gray-300';
  };

  const formatTime = (seconds: number) => {
    return seconds.toString().padStart(3, '0');
  };

  return (
    <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-500 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 bg-gray-600 rounded-lg p-3 border-2 border-gray-700">
        <div className="flex items-center gap-4">
          <h2 className="text-white font-bold text-lg">Minesweeper</h2>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={initializeGame}
            className="bg-gray-500 text-white border-gray-400 hover:bg-gray-400"
          >
            <RotateCcw size={16} className="mr-1" />
            New Game
          </Button>
          {onClose && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="bg-gray-500 text-white border-gray-400 hover:bg-gray-400"
            >
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Game Info */}
      <div className="flex justify-center mb-4">
        <div className="bg-black text-green-400 px-6 py-2 rounded font-mono text-xl border-2 border-gray-600 flex items-center gap-8">
          <div className="flex items-center gap-2">
            <Bomb className="w-5 h-5" />
            {(MINES - flagCount).toString().padStart(3, '0')}
          </div>
          
          <div className={`px-4 py-1 rounded ${
            gameState === 'won' ? 'bg-green-600' :
            gameState === 'lost' ? 'bg-red-600' : 'bg-yellow-600'
          }`}>
            {gameState === 'won' ? <Trophy className="w-6 h-6 text-white" /> :
             gameState === 'lost' ? <Skull className="w-6 h-6 text-white" /> :
             '🙂'}
          </div>
          
          <div>
            {formatTime(timer)}
          </div>
        </div>
      </div>

      {/* Game Board */}
      <div className="flex justify-center">
        <div className="inline-block border-4 border-gray-600 bg-gray-600">
          {board.map((row, rowIndex) => (
            <div key={rowIndex} className="flex">
              {row.map((cell, colIndex) => (
                <button
                  key={`${rowIndex}-${colIndex}`}
                  className={`
                    w-8 h-8 border-2 flex items-center justify-center text-sm font-bold
                    ${getCellStyle(cell)}
                    ${gameState === 'playing' ? 'cursor-pointer' : 'cursor-default'}
                  `}
                  onClick={() => revealCell(rowIndex, colIndex)}
                  onContextMenu={(e) => toggleFlag(e, rowIndex, colIndex)}
                  disabled={gameState !== 'playing'}
                >
                  {getCellContent(cell)}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Win/Lose Message */}
      {gameState !== 'playing' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center">
            {gameState === 'won' ? (
              <>
                <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">You Win!</h3>
                <p className="text-gray-600 mb-4">Time: {formatTime(timer)} seconds</p>
              </>
            ) : (
              <>
                <Skull className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">Game Over!</h3>
                <p className="text-gray-600 mb-4">You hit a mine!</p>
              </>
            )}
            <Button onClick={initializeGame}>Play Again</Button>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 bg-gray-600 rounded-lg p-4 text-white">
        <h3 className="font-semibold mb-2">How to Play:</h3>
        <ul className="text-sm space-y-1 text-gray-200">
          <li>• Left click to reveal cells</li>
          <li>• Right click to flag potential mines</li>
          <li>• Numbers show how many mines are adjacent</li>
          <li>• Find all mines without clicking on them</li>
          <li>• Win by revealing all non-mine cells</li>
        </ul>
      </div>
    </div>
  );
};